// Human-readable duration + travellers strings shared between the
// customer-facing PDF/PPTX export and the consultant-facing planner.
// Pure JS — no React, no DOM, no schema dependency — so both
// `apps/preview` and `apps/itineraries` (and any future surface that
// needs the same labels) can call into it.
//
// Mirrors the legacy `getDurationAndTravallers` (sic) in
// `trip_planner/lib/utils.ts:605-705` so the wire format stays stable.

export type DurationAndTravellers = {
  duration: string;
  travellers: string;
};

// Minimal structural shape — accepts the union of `PreviewItem` /
// `ItineraryItem`. `travellers` stays `unknown` because the column
// jsonb has multiple historical shapes (see `formatTravellersJson`).
export type DurationTravellersInput = {
  startTime: string | null;
  endTime: string | null;
  travellers: unknown;
};

// Item kind drives the unit. Legacy `getDurationAndTravallers`
// (`lib/utils.ts:646-682`) renders:
//   - 'accommodation' → "X nights" via getDifferenceInNights
//   - 'rental'        → "X days" via getDifferenceInDays
//   - 'other'         → "Xh Ym" / "Xd Yh Zm" via raw ms diff
export type DurationKind = 'accommodation' | 'rental' | 'other';

// Item duration in ms → "Xh Ym" / "Xd Yh Zm" / "Z min" string.
// Durations ≥ 24h are split into "Xd Yh Zm" (legacy lines 666-676);
// shorter durations stay in hours+minutes (legacy lines 677-682);
// sub-hour durations render as "Z min" (matches legacy fallback when
// `hours === 0`).
export const formatDurationMs = (ms: number | null): string => {
  if (ms === null || ms <= 0) return '';
  const totalMinutes = Math.round(ms / 60_000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours === 0) return `${minutes} min`;
  if (hours >= 24) {
    const days = Math.floor(hours / 24);
    const remHours = hours % 24;
    return [
      `${days}d`,
      remHours > 0 ? `${remHours}h` : '',
      minutes > 0 ? `${minutes}m` : '',
    ]
      .filter(Boolean)
      .join(' ')
      .trim();
  }
  if (minutes === 0) return `${hours}h`;
  return `${hours}h ${minutes}m`;
};

// "12 Adults, 2 Children" projection over the loose `travellers` jsonb.
// Three shapes supported, in order of preference:
//
//   1. Array of `{ quantity, travelerType: { title } }` — the shape used
//      by `itinerary_version.travellers` (legacy
//      `app/preview/[id]/layout.tsx:60-73`).
//   2. `{ people: Traveler[] }` with each `{ title }` counted — the shape
//      used by `item.travellers` (legacy `getTravelerCounts` in
//      `lib/utils.ts:391-406`).
//   3. Legacy fallback `{ adults?, children?, infants? }` — kept for
//      synthetic test fixtures; not seen in production data.
//
// `Children` is sorted last to match legacy ordering. Returns the empty
// string when no travellers are present so callers can decide whether
// to render a fallback (legacy uses "Not specified" in the per-item
// surface).
export const formatTravellersJson = (raw: unknown): string => {
  if (!raw) return '';

  if (Array.isArray(raw)) {
    return joinCounts(collectFromQuantityArray(raw));
  }

  if (typeof raw !== 'object') return '';
  const obj = raw as Record<string, unknown>;

  if (Array.isArray(obj.people)) {
    return joinCounts(collectFromPeopleArray(obj.people as unknown[]));
  }

  const adults = typeof obj.adults === 'number' ? obj.adults : 0;
  const children = typeof obj.children === 'number' ? obj.children : 0;
  const infants = typeof obj.infants === 'number' ? obj.infants : 0;
  const counts: { title: string; quantity: number }[] = [];
  if (adults > 0) counts.push({ title: 'Adults', quantity: adults });
  if (children > 0) counts.push({ title: 'Children', quantity: children });
  if (infants > 0) counts.push({ title: 'Infants', quantity: infants });
  return joinCounts(counts);
};

const collectFromQuantityArray = (
  raw: unknown[]
): { title: string; quantity: number }[] => {
  const out: { title: string; quantity: number }[] = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') continue;
    const row = entry as Record<string, unknown>;
    const quantity = typeof row.quantity === 'number' ? row.quantity : 0;
    if (quantity <= 0) continue;
    const travelerType =
      row.travelerType && typeof row.travelerType === 'object'
        ? (row.travelerType as Record<string, unknown>)
        : null;
    const title =
      typeof travelerType?.title === 'string'
        ? travelerType.title
        : typeof row.title === 'string'
          ? row.title
          : null;
    if (!title) continue;
    out.push({ title, quantity });
  }
  return out;
};

const collectFromPeopleArray = (
  people: unknown[]
): { title: string; quantity: number }[] => {
  const byTitle = new Map<string, number>();
  for (const person of people) {
    if (!person || typeof person !== 'object') continue;
    const row = person as Record<string, unknown>;
    const title =
      typeof row.title === 'string'
        ? row.title
        : row.travelerType &&
            typeof row.travelerType === 'object' &&
            typeof (row.travelerType as Record<string, unknown>).title ===
              'string'
          ? ((row.travelerType as Record<string, unknown>).title as string)
          : null;
    if (!title) continue;
    byTitle.set(title, (byTitle.get(title) ?? 0) + 1);
  }
  return Array.from(byTitle.entries()).map(([title, quantity]) => ({
    title,
    quantity,
  }));
};

const joinCounts = (
  counts: ReadonlyArray<{ title: string; quantity: number }>
): string => {
  if (counts.length === 0) return '';
  const sorted = [...counts].sort((a, b) => {
    if (a.title === 'Children') return 1;
    if (b.title === 'Children') return -1;
    return 0;
  });
  return sorted.map((c) => `${c.quantity} ${c.title}`).join(', ');
};

// Top-level helper used by both the cover slide (itinerary-level
// travellers) and per-item rows. Returns "" when no travellers
// resolved; callers can substitute their own placeholder ("Not
// specified" in the legacy per-item surface).
export const formatItineraryTravellers = (raw: unknown): string =>
  formatTravellersJson(raw);

// UTC-midnight diff in calendar days. Matches legacy `getDifferenceInDays`
// (`lib/utils.ts:528-544`): floors each timestamp to its UTC date and
// adds 1 so a same-day rental still reads as "1 day".
const utcCalendarDaysInclusive = (startIso: string, endIso: string): number => {
  const a = new Date(startIso);
  const b = new Date(endIso);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return 0;
  const aMid = Date.UTC(a.getUTCFullYear(), a.getUTCMonth(), a.getUTCDate());
  const bMid = Date.UTC(b.getUTCFullYear(), b.getUTCMonth(), b.getUTCDate());
  const diff = Math.abs(bMid - aMid) / (1000 * 60 * 60 * 24);
  return Math.round(diff) + 1;
};

// UTC-midnight diff in nights. Matches legacy `getDifferenceInNights`
// (`lib/utils.ts:546-562`): same calendar-day diff WITHOUT the +1, so a
// same-day stay reads as 0 nights.
const utcNights = (startIso: string, endIso: string): number => {
  const a = new Date(startIso);
  const b = new Date(endIso);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return 0;
  const aMid = Date.UTC(a.getUTCFullYear(), a.getUTCMonth(), a.getUTCDate());
  const bMid = Date.UTC(b.getUTCFullYear(), b.getUTCMonth(), b.getUTCDate());
  return Math.round(Math.abs(bMid - aMid) / (1000 * 60 * 60 * 24));
};

// Per-item duration + travellers — drop-in replacement for legacy
// `getDurationAndTravallers(item, isAccommodation, isRental)`. Accepts
// the minimal structural shape (`startTime`, `endTime`, `travellers`) so
// both `PreviewItem` and `ItineraryItem` can pass through without a cast.
//
// `kind` defaults to 'other' (raw hour/minute format). Pass
// 'accommodation' for hotel rows ("3 nights") or 'rental' for rental-car
// rows ("3 days").
export const getDurationAndTravellers = (
  item: DurationTravellersInput,
  kind: DurationKind = 'other'
): DurationAndTravellers => {
  if (kind === 'accommodation' && item.startTime && item.endTime) {
    const nights = utcNights(item.startTime, item.endTime);
    return {
      duration:
        nights > 0 ? `${nights} ${nights === 1 ? 'night' : 'nights'}` : '',
      travellers: formatTravellersJson(item.travellers),
    };
  }
  if (kind === 'rental' && item.startTime && item.endTime) {
    const days = utcCalendarDaysInclusive(item.startTime, item.endTime);
    return {
      duration: days > 0 ? `${days} ${days === 1 ? 'day' : 'days'}` : '',
      travellers: formatTravellersJson(item.travellers),
    };
  }
  let durationMs: number | null = null;
  if (item.startTime && item.endTime) {
    const start = new Date(item.startTime).getTime();
    const end = new Date(item.endTime).getTime();
    if (Number.isFinite(start) && Number.isFinite(end) && end > start) {
      durationMs = end - start;
    }
  }
  return {
    duration: formatDurationMs(durationMs),
    travellers: formatTravellersJson(item.travellers),
  };
};

// Classify an item for `getDurationAndTravellers`. Mirrors legacy
// detection: contentCategory === 'Accommodation' for hotels;
// contentSubcategory === 'RentalCar' for rental cars; everything else
// falls back to 'other'.
export const durationKindOf = (item: {
  contentCategory: string | null;
  contentSubcategory: string | null;
}): DurationKind => {
  if (item.contentCategory === 'Accommodation') return 'accommodation';
  if (item.contentSubcategory === 'RentalCar') return 'rental';
  return 'other';
};
